import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { cn } from "@/lib/utils";

export interface FormActionsProps {
  /** Route to navigate to on Cancel (e.g. the pilot's list). Ignored when `onCancel` is set. */
  cancelTo?: string;
  /** Tab/page title for `cancelTo` in workspace mode. */
  cancelTitle?: string;
  onCancel?: () => void;
  cancelLabel?: string;
  submitLabel: string;
  /** Shows a spinner on Submit and disables both buttons. */
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Submit is `type="submit"`; pass `onSubmit` only when the actions are outside a `<form>`. */
  onSubmit?: () => void;
  /** Extra content on the left (e.g. a validation hint). */
  children?: ReactNode;
  className?: string;
}

/** Footer for create/edit forms: right-aligned Cancel (outline) + Submit (default). */
export function FormActions({
  cancelTo,
  cancelTitle,
  onCancel,
  cancelLabel = "Cancel",
  submitLabel,
  submitting = false,
  submitDisabled = false,
  onSubmit,
  children,
  className,
}: FormActionsProps) {
  const navigate = useAppNavigate();
  const showCancel = onCancel !== undefined || cancelTo !== undefined;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (cancelTo) {
      navigate({ to: cancelTo, title: cancelTitle });
    }
  };

  return (
    <div
      data-slot="form-actions"
      className={cn("flex flex-wrap items-center justify-end gap-2 pt-2", className)}
    >
      {children ? <div className="me-auto min-w-0 text-sm text-muted-foreground">{children}</div> : null}
      {showCancel ? (
        <Button type="button" variant="outline" onClick={handleCancel} disabled={submitting}>
          {cancelLabel}
        </Button>
      ) : null}
      <Button
        type={onSubmit ? "button" : "submit"}
        onClick={onSubmit}
        disabled={submitDisabled || submitting}
        aria-busy={submitting || undefined}
      >
        {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
        {submitLabel}
      </Button>
    </div>
  );
}
